import { useEffect, useRef, useState } from 'react';
import { api } from '../utils/api.js';
import { RATING_REASONS } from '../../shared/feedback.js';

// 👍 / 👎 on one AI reply (D-LAUNCH3). A thumb is saved the moment it's
// tapped, so an abandoned "why" still counts; the reason and note re-save it.
// Tapping the chosen thumb again clears it. Muted/borderless and right-aligned
// so it can't be mistaken for the Yes/No answer chips (Task 9 review).
export default function TurnRating({ turnId, initial = null }) {
  const [rating, setRating] = useState(initial?.rating ?? 0);
  const [reason, setReason] = useState(null);
  const [note, setNote] = useState('');
  const [askWhy, setAskWhy] = useState(false);
  const [thanks, setThanks] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  // Once the tech has tapped a thumb locally, a late-arriving /ratings
  // response (which fired before the tap, on session load) must never
  // clobber it back to whatever was on the server a moment ago.
  const touched = useRef(false);

  // Ratings load after the transcript; adopt them when they arrive — unless
  // the tech has already acted on this turn since mount.
  useEffect(() => {
    if (initial && !touched.current) {
      setRating(initial.rating);
    }
  }, [initial?.rating, initial?.category]);

  // "Thanks — noted." is a fleeting confirmation, not a persistent label.
  useEffect(() => {
    if (!thanks) return;
    const id = setTimeout(() => setThanks(false), 2000);
    return () => clearTimeout(id);
  }, [thanks]);

  async function save(next, extra = {}) {
    const prev = rating;
    setBusy(true);
    setFailed(false);
    setRating(next);
    try {
      await api.rateTurn({ turnId, rating: next, category: extra.category ?? null, message: extra.message ?? null });
      return true;
    } catch {
      setRating(prev);
      setFailed(true);
      return false;
    } finally {
      setBusy(false);
    }
  }

  async function tap(value) {
    if (busy) return;
    touched.current = true;
    setThanks(false);
    if (rating === value) {
      if (await save(0)) {
        setAskWhy(false);
        setReason(null);
      }
      return;
    }
    if (await save(value)) {
      setReason(null);
      setAskWhy(value === -1);
      if (value === 1) setThanks(true);
    }
  }

  async function sendWhy() {
    touched.current = true;
    if (await save(-1, { category: reason, message: note.trim() || null })) {
      setAskWhy(false);
      setNote('');
      setThanks(true);
    }
  }

  const thumb = (active, activeClass) =>
    `min-h-[44px] min-w-[44px] rounded-lg text-lg transition ${
      active ? `${activeClass} opacity-100` : 'opacity-60'
    }`;

  const whyHeadingId = `why-${turnId}`;

  return (
    <div className="mt-2">
      <div className="flex items-center justify-end gap-2">
        <span className="text-ink-400 text-xs">Rate this reply</span>
        <button type="button" aria-label="Good reply" aria-pressed={rating === 1} disabled={busy} onClick={() => tap(1)} className={thumb(rating === 1, 'bg-pass/20')}>👍</button>
        <button type="button" aria-label="Bad reply" aria-pressed={rating === -1} disabled={busy} onClick={() => tap(-1)} className={thumb(rating === -1, 'bg-fail/20')}>👎</button>
        {thanks && (
          <span role="status" className="text-ink-400 text-xs">Thanks — noted.</span>
        )}
        {failed && (
          <span role="status" className="text-fail-400 text-xs">Didn't save — try again.</span>
        )}
      </div>
      {askWhy && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            sendWhy();
          }}
          className="mt-2 space-y-2 rounded-xl border border-ink-700 p-3"
        >
          <p id={whyHeadingId} className="text-ink-200 text-sm font-semibold">What was wrong with it?</p>
          <div role="group" aria-labelledby={whyHeadingId} className="flex flex-wrap gap-2">
            {RATING_REASONS.map((r) => (
              <button
                key={r.id}
                type="button"
                aria-pressed={reason === r.id}
                onClick={() => setReason(reason === r.id ? null : r.id)}
                className={`min-h-[44px] px-3 rounded-lg border text-sm ${
                  reason === r.id ? 'bg-mra-red/20 border-mra-red text-ink-50' : 'border-ink-700 text-ink-200'
                }`}
              >
                {r.label}
              </button>
            ))}
          </div>
          <input
            type="text"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Optional: what should it have said?"
            aria-label="What should it have said?"
            className="w-full min-h-[44px] px-3 rounded-lg bg-ink-900 border border-ink-700 text-ink-50"
          />
          <div className="flex gap-2">
            <button type="submit" disabled={busy || (!reason && !note.trim())} className="btn-primary flex-1 !min-h-[44px] !py-2">Send</button>
            <button type="button" onClick={() => setAskWhy(false)} className="btn-ghost">Skip</button>
          </div>
        </form>
      )}
    </div>
  );
}
